import React from 'react';
import { Card } from '@/designSystem/Card';
import { LineChart, BarChart3, CalendarClock } from 'lucide-react';
import PriceChart from '../../charts/components/PriceChart';
import RankChart from '../../charts/components/RankChart';
import { ChartCompetitor, TimeseriesDataPoint } from '@/features/charts/types';
import { getCurrencySymbolFromCountryCode } from '@/utils';
import { DeviceType } from '../hooks/useScrapedPropertyWithFilters';

interface ChartRefs {
  pricingChartRef: React.RefObject<HTMLDivElement>;
  rankingChartRef: React.RefObject<HTMLDivElement>;
}

interface PropertyPerformanceProps {
  competitorMetrics?: ChartCompetitor[];
  propertyDatapoints?: TimeseriesDataPoint[];
  visiblePropertyIds: number[];
  countryCode?: string;
  chartRefs?: ChartRefs;
  latestScrapeDates?: Record<string, string>;
  deviceType?: DeviceType;
}

const PropertyPerformance: React.FC<PropertyPerformanceProps> = ({
  competitorMetrics = [],
  countryCode,
  propertyDatapoints = [],
  visiblePropertyIds,
  chartRefs,
  latestScrapeDates = {},
  deviceType
}) => {
  // Filter competitor metrics based on visibility
  const filteredCompetitors = React.useMemo(() => {
    if (!competitorMetrics?.length || !visiblePropertyIds.length) return [];

    return competitorMetrics.filter(metric =>
      visiblePropertyIds.includes(metric.property_id)
    );
  }, [competitorMetrics, visiblePropertyIds]);

  // Format the scrape dates for display
  const formatScrapeDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (e) {
      return '';
    }
  };

  // Get latest scrape date from any available group
  const latestScrapeDate = React.useMemo(() => {
    const dates = Object.values(latestScrapeDates || {}).filter(Boolean);
    if (!dates.length) return '';

    // Find the most recent date
    const mostRecentDate = new Date(
      Math.max(...dates.map(dateStr => new Date(dateStr).getTime()))
    );

    return formatScrapeDate(mostRecentDate.toISOString());
  }, [latestScrapeDates]);

  return (
    <Card>
      <div className="bg-blue-50 p-4 border-b border-blue-100 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold flex items-center">
            <LineChart className="w-5 h-5 mr-2 text-blue-600" />
            Performance Charts
          </h3>
          <p className="text-sm text-gray-600 ml-7">
            These charts show how your price and search ranking compare to competitors over time. Find leaks in your pricing & issues with your ranking.
          </p>
        </div>
      </div>

      <div className="p-4">
        <div ref={chartRefs?.rankingChartRef}>
          <div className="mb-2 p-2 border-l-4 border-purple-500 pl-2">
            <h4 className="font-semibold flex items-center text-lg">
              <BarChart3 size={18} className="mr-2 text-purple-500" />
              Rank Comparison
            </h4>
            <p className="text-sm text-gray-600 ml-7">
              Your property's position in search results compared to competitors.
            </p>

            {/* Scrape date information */}
            {latestScrapeDate && (
              <div className="flex items-center text-xs text-gray-600 mt-1 ml-7">
                <CalendarClock size={12} className="mr-1 text-blue-600" />
                <span className="font-medium">Last updated:</span>
                <span className="ml-1">{latestScrapeDate}</span>
              </div>
            )}
          </div>
        </div>

        <RankChart
          propertyDatapoints={propertyDatapoints}
          competitors={filteredCompetitors}
        />

        <PriceChart
          propertyDatapoints={propertyDatapoints}
          competitors={filteredCompetitors}
          currencySymbol={countryCode ? getCurrencySymbolFromCountryCode(countryCode) : ''}
          deviceType={deviceType}
        />
      </div>
    </Card>
  );
};

export default PropertyPerformance;